import { QBankStructureLevelTag } from '@exam-management/components';
import {
  QBankStructureLevelCategory,
  useGetLearningObjectiveStatementByIdQuery,
} from '@exam-management/graphql';
import { getQuestionSourceForLOS } from '@exam-management/los-list';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Chip, Divider, EllipsisText, Flex, Skeleton, Text } from '@shared/components';
import { useMemo } from 'react';

interface LOSDetailsProps {
  losId: string;
}

export default function LOSDetails(props: Readonly<LOSDetailsProps>) {
  const { losId } = props;

  const { qBankStructureInformation, qBankStructureLevelItems } = useQBankStructure();

  const { data, loading } = useGetLearningObjectiveStatementByIdQuery({
    variables: {
      input: {
        id: losId,
      },
    },
  });

  const los = data?.qbsGetLearningObjectiveStatementById;

  const chapterLevelItem = los?.qBankStructureLevelItem;

  const qSource = useMemo(
    () =>
      chapterLevelItem
        ? getQuestionSourceForLOS(
            qBankStructureInformation.code,
            qBankStructureLevelItems,
            chapterLevelItem.id,
          )
        : undefined,
    [chapterLevelItem, qBankStructureInformation.code, qBankStructureLevelItems],
  );

  return (
    <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
      <Flex
        sx={(theme) => ({
          border: `1px solid ${theme.palette.grey[200]}`,
          flexDirection: 'column',
          p: theme.spacing(4),
          borderRadius: theme.shape.rounded(2),
          backgroundColor: theme.palette.grey[50],
          gap: theme.spacing(4),
        })}
      >
        <Flex sx={{ width: '100%' }}>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
              width: '100%',
              alignItems: 'center',
            })}
          >
            {loading ? (
              <Skeleton width='40%' />
            ) : (
              <>
                <QBankStructureLevelTag category={QBankStructureLevelCategory.Chapter} />
                <Divider orientation='vertical' variant='middle' flexItem />
                <EllipsisText variant='body1Semibold' sx={{ flex: 1 }}>
                  {chapterLevelItem?.tag?.name ?? '-'}
                </EllipsisText>
              </>
            )}
          </Flex>
          {loading ? (
            <Skeleton width={120} />
          ) : (
            <Chip
              label={`QSource: ${qSource ?? '-'}`}
              size='small'
              sx={(theme) => ({
                border: `1px solid ${theme.palette.grey[300]}`,
                backgroundColor: theme.palette.grey[200],
              })}
            />
          )}
        </Flex>
        <Divider />
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
          <Text variant='body1Semibold'>วัตถุประสงค์การเรียนรู้ (LOS)</Text>
          {loading ? <Skeleton /> : <Text variant='body1'>{los?.tag.name ?? '-'}</Text>}
        </Flex>
      </Flex>
    </Flex>
  );
}
